/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions */
import { ArrowUpRight, ChevronDown, LayoutDashboard } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ctaDestination, trackEvent } from "@/client/analytics/track";
import { CookieSettingsLink } from "@/client/components/cookie-settings/CookieSettings";
import { TheResumeLogo } from "@/client/components/theResumeLogo";
import { env } from "@/client/config/env";
import { useAuthStore } from "@/client/stores/auth";
import { apex25HomeUrl, recruiterSignInUrl } from "@/client/utils/hubUrls";

import { footer } from "./content";

const track = (id: string, href: string) =>
  trackEvent("cta_clicked", {
    cta_id: id,
    cta_location: "hub_chrome",
    destination: ctaDestination(href),
  });

/** Log in for either side: theResume for job seekers, theRecruit for hiring teams. */
const LogInMenu = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="hub-menu" ref={ref}>
      <button
        type="button"
        className="hub-btn hub-btn--quiet"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        Log in <ChevronDown size={14} aria-hidden="true" data-open={open} />
      </button>
      {open && (
        <div className="hub-menu__panel">
          <Link to="/auth/login" onClick={() => track("hub_login_candidate", "/auth/login")}>
            <span>Job seekers</span>
            <small>theResume</small>
          </Link>
          <a
            href={recruiterSignInUrl()}
            onClick={() => track("hub_login_recruiter", recruiterSignInUrl())}
          >
            <span>Hiring teams</span>
            <small>Apex 25</small>
          </a>
        </div>
      )}
    </div>
  );
};

export const HubHeader = () => {
  const signedIn = useAuthStore((state) => Boolean(state.user));
  const [scrolled, setScrolled] = useState(false);
  // The first fold carries the big logo, so the header's own logo and nav wait until it has gone.
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      setCompact(window.scrollY > window.innerHeight * 0.45);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="hub-header" data-scrolled={scrolled} data-compact={compact}>
      <div className="hub-shell hub-header__inner">
        <Link to="/" aria-label="theResume home" className="hub-logo">
          <TheResumeLogo size={132} />
        </Link>
        <nav className="hub-nav" aria-label="Primary">
          <Link to="/job-seekers">For job seekers</Link>
          <a href={apex25HomeUrl()}>
            For hiring teams <ArrowUpRight size={13} aria-hidden="true" />
          </a>
        </nav>
        {signedIn ? (
          <Link to="/dashboard" className="hub-btn hub-btn--quiet">
            <LayoutDashboard size={15} aria-hidden="true" /> Go to dashboard
          </Link>
        ) : (
          <LogInMenu />
        )}
      </div>
    </header>
  );
};

export const HubFooter = () => (
  <footer className="hub-footer">
    <div className="hub-shell">
      <div className="hub-footer__grid">
        <div>
          <p className="hub-footer__brand">theResume</p>
          <p className="hub-footer__tag">{footer.tagline}</p>
        </div>
        <div>
          <p className="hub-footer__head">{footer.candidateHeading}</p>
          <ul>
            {footer.candidateLinks.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="hub-footer__head">{footer.recruiterHeading}</p>
          <ul>
            <li>
              <a href={apex25HomeUrl()}>
                Apex 25 <ArrowUpRight size={12} aria-hidden="true" />
              </a>
            </li>
            <li>
              <a href={recruiterSignInUrl()}>Hiring team log in</a>
            </li>
          </ul>
        </div>
        <div>
          <p className="hub-footer__head">{footer.companyHeading}</p>
          <ul>
            {footer.companyLinks.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.label}</Link>
              </li>
            ))}
            <li>
              <a href={`mailto:${env.VITE_SUPPORT_EMAIL}`}>{env.VITE_SUPPORT_EMAIL}</a>
            </li>
          </ul>
        </div>
      </div>
      <p className="hub-footer__legal">
        © 2026 theResume. All rights reserved. · <CookieSettingsLink />
      </p>
    </div>
  </footer>
);
